import * as Y from "yjs"
import { WebsocketProvider } from "y-websocket"
import {
  parseQuestionnaireVariant,
  updateQuestionnaireWithVariant,
} from "utils/variantUtils"
import { htmlToPlainText, stripCommentSpans } from "utils/editorUtils"
import { Variant } from "types/questionnaire.types"

interface CollaborationConfig {
  websocketUrl: string
  projectId: string
  userId: string
}

interface VariantDoc {
  ydoc: Y.Doc
  provider: WebsocketProvider | null
  isConnected: boolean
}

interface VariantDocuments {
  [variantName: string]: VariantDoc
}

interface VariantMetadata {
  name: string
  languages: string[]
  lastModified?: string
}

interface ProviderStatusEvent {
  status: string
}

export class CollaborationService {
  private config: CollaborationConfig
  private variantDocs: VariantDocuments = {}
  private currentVariant: string = "master"
  private onDocumentUpdate?: (variantName: string, content: string) => void
  private onVariantChange?: (variants: Variant[]) => void
  private onQuestionnaireRefresh?: () => void
  private variantsDoc: Y.Doc | null = null
  private variantsProvider: WebsocketProvider | null = null

  constructor(config: CollaborationConfig) {
    this.config = config
    this.initializeVariantsMetadata()
  }

  /**
   * Initialize the shared variants metadata document
   */
  private initializeVariantsMetadata(): void {
    const variantsDocKey = `${this.config.projectId}-variants-meta`

    this.variantsDoc = new Y.Doc()
    this.variantsProvider = new WebsocketProvider(
      this.config.websocketUrl,
      variantsDocKey,
      this.variantsDoc,
      {
        connect: true,
        resyncInterval: 20000,
      }
    )

    // Set awareness state to prevent y-websocket's 30-second idle disconnect
    this.variantsProvider.awareness.setLocalState({ user: { name: this.config.userId } })

    // Add connection event listeners
    this.variantsProvider.on("status", (event: ProviderStatusEvent) => {
      // Only log errors, not regular status updates
      if (event.status === "disconnected" || event.status === "error") {
        console.warn("Variants provider status:", event.status)
      }
    })

    this.variantsProvider.on("connection-error", (event: Event) => {
      console.error("Variants provider connection error:", event)
    })

    // Listen for changes to the variants map
    const variantsMap = this.variantsDoc.getMap("variants")
    variantsMap.observe((event, transaction) => {
      // Only react to remote changes (from other browsers)
      // transaction.origin === null means it's a local change, which we should ignore
      if (transaction?.origin !== null) {
        // Remote change - update variants list only
        // No need to trigger full questionnaire refresh since backend is source of truth
        // and content sync happens through Yjs document collaboration
        if (this.onVariantChange) {
          const variants = this.getVariantsFromMetadata()
          this.onVariantChange(variants)
        }
      }
    })
  }

  /**
   * Pre-initialize all variant rooms upfront
   * This creates connections to all variants at once, keeping them open until project is closed
   * @param variants - List of all variants in the project
   */
  initializeAllVariants(variants: Variant[]): void {
    // Initialize master variant first
    this.initializeVariant("master")

    // Initialize all other variants
    variants.forEach((variant) => {
      if (variant.name && variant.name !== "master") {
        this.initializeVariant(variant.name)
      }
    })
  }

  /**
   * Check if a variant doc has remote content (from other users)
   * @returns true if the Yjs doc has content synced from other users
   */
  hasRemoteContent(variantName: string): boolean {
    const variantDoc = this.variantDocs[variantName]
    if (!variantDoc || !variantDoc.ydoc) {
      return false
    }

    const fragment = variantDoc.ydoc.getXmlFragment("default")
    return fragment.length > 0
  }

  /**
   * Initialize collaboration for a specific variant
   */
  initializeVariant(variantName: string): Y.Doc {
    const docKey = this.getDocumentKey(variantName)

    if (this.variantDocs[variantName]) {
      // Don't create a new provider if one already exists
      return this.variantDocs[variantName].ydoc
    }

    const ydoc = new Y.Doc()

    // Initialize the XMLFragment that TipTap expects
    ydoc.getXmlFragment("default")

    // Create WebSocket provider for real-time sync.
    // No local persistence: the server is the sole source of truth. Local
    // IndexedDB caused cross-session ops to merge with live server content,
    // producing silent permanent duplication. The editor is disabled on
    // disconnect to compensate (see Editor.tsx).
    const provider = new WebsocketProvider(
      this.config.websocketUrl,
      docKey,
      ydoc,
      {
        connect: true,
        resyncInterval: 20000,
      }
    )

    // Set awareness state so the provider sends periodic updates,
    // preventing y-websocket's 30-second idle disconnect
    provider.awareness.setLocalState({ user: { name: this.config.userId } })

    this.variantDocs[variantName] = {
      ydoc,
      provider,
      isConnected: false,
    }

    // Add connection event listeners
    provider.on("status", (event: ProviderStatusEvent) => {
      if (this.variantDocs[variantName]) {
        this.variantDocs[variantName].isConnected = event.status === "connected"
      }
    })

    provider.on("connection-close", () => {
      if (this.variantDocs[variantName]) {
        this.variantDocs[variantName].isConnected = false
      }
    })

    provider.on("connection-error", () => {
      if (this.variantDocs[variantName]) {
        this.variantDocs[variantName].isConnected = false
      }
    })

    // Set up document update listener
    ydoc.on("update", (update: Uint8Array, origin: unknown) => {
      // Only trigger callbacks for remote updates (not local ones)
      if (origin !== null && this.onDocumentUpdate) {
        const content = this.getVariantContentAsHTML(variantName)
        this.onDocumentUpdate(variantName, content)
      }
    })

    return ydoc
  }

  /**
   * Switch to a different variant for collaboration
   * Now just retrieves the existing doc (all docs are pre-initialized)
   */
  switchToVariant(variantName: string): Y.Doc {
    this.currentVariant = variantName

    // If doc already exists, just return it (no new provider creation)
    const existingDoc = this.variantDocs[variantName]
    if (existingDoc) {
      return existingDoc.ydoc
    }

    // Fallback: initialize if somehow not pre-initialized
    return this.initializeVariant(variantName)
  }

  /**
   * Get the current variant's Yjs document
   */
  getCurrentDoc(): Y.Doc | null {
    const variantDoc = this.variantDocs[this.currentVariant]
    return variantDoc ? variantDoc.ydoc : null
  }

  /**
   * Get a specific variant's Yjs document
   */
  getVariantDoc(variantName: string): Y.Doc | null {
    const variantDoc = this.variantDocs[variantName]
    return variantDoc ? variantDoc.ydoc : null
  }

  /**
   * Get the WebSocket provider for a specific variant
   */
  getProvider(variantName: string): WebsocketProvider | null {
    const variantDoc = this.variantDocs[variantName]
    return variantDoc ? variantDoc.provider : null
  }

  /**
   * Get a variant's Yjs content as plain text for saving.
   * Converts Yjs HTML to the same text format as htmlToFilteredText in Editor.tsx.
   */
  getVariantContentAsText(variantName: string): string {
    const html = this.getVariantContentAsHTML(variantName)
    if (!html || html === "<p></p>") return ""
    return htmlToPlainText(stripCommentSpans(html))
  }

  /**
   * Convert Yjs document content to HTML for a specific variant
   * This method will be called by the editor to get HTML content
   */
  getVariantContentAsHTML(variantName: string): string {
    const variantDoc = this.variantDocs[variantName]
    if (!variantDoc || !variantDoc.ydoc) return ""

    try {
      // Get the prosemirror fragment from Yjs
      const fragment = variantDoc.ydoc.getXmlFragment("default")

      // Check if fragment has any content
      if (fragment.length === 0) {
        return ""
      }

      // Convert to HTML using improved extraction
      const html = this.prosemirrorToHTML(fragment)
      return html
    } catch {
      return ""
    }
  }

  /**
   * Convert prosemirror XML fragment to HTML
   * Improved version that handles nested content and different node types
   */
  private prosemirrorToHTML(fragment: Y.XmlFragment): string {
    let html = ""

    fragment.forEach((item) => {
      html += this.convertYjsItemToHTML(item)
    })

    return html || "<p></p>"
  }

  /**
   * Convert a single Yjs item to HTML
   */
  private convertYjsItemToHTML(item: Y.XmlElement | Y.XmlText): string {
    if (item instanceof Y.XmlElement) {
      const tagName = item.nodeName || "p"
      const textContent = this.extractTextContent(item)

      // Handle different node types
      switch (tagName) {
        case "paragraph":
          return `<p>${textContent}</p>`
        case "hardBreak":
          return "<br>"
        default:
          return `<p>${textContent}</p>`
      }
    } else if (item instanceof Y.XmlText) {
      return item.toString()
    }
    return ""
  }

  /**
   * Extract text content from XML element recursively
   */
  private extractTextContent(element: Y.XmlElement): string {
    let text = ""
    element.forEach((item) => {
      if (item instanceof Y.XmlText) {
        text += item.toString()
      } else if (item instanceof Y.XmlElement) {
        // Handle nested elements
        if (item.nodeName === "hardBreak") {
          text += "\n"
        } else {
          text += this.extractTextContent(item)
        }
      }
    })
    return text
  }

  /**
   * Convert all collaborative documents back to the original text format
   * Uses fresh fullQuestionnaire from backend as base, only replaces current variant content
   */
  convertToTextFormat(
    currentContent: string,
    variants: Variant[],
    currentVariant: string = "master",
    fullQuestionnaire: string = ""
  ): string {
    let result = ""

    // If we're editing master, use the current content as master base
    if (currentVariant === "master" || currentVariant === "") {
      result = currentContent
    } else {
      // We're editing a variant — get master content from Yjs doc first, fallback to backend
      const masterYjsContent = this.getVariantContentAsText("master")
      if (masterYjsContent) {
        result = masterYjsContent
      } else if (fullQuestionnaire) {
        const masterContentLines: string[] = []
        let insideVariant = false

        fullQuestionnaire.split("\n").forEach((line: string) => {
          if (line.startsWith("#DEFINE VARIANT")) {
            insideVariant = true
          } else if (insideVariant && line.trim() === "") {
            return
          } else if (insideVariant && !line.startsWith("#")) {
            return
          } else if (!insideVariant) {
            masterContentLines.push(line)
          }
        })

        result = masterContentLines.join("\n").trim()
      } else {
        result = ""
      }
    }

    // Parse variants from the fresh fullQuestionnaire to get their current content
    const freshVariants = fullQuestionnaire ? parseQuestionnaireVariant(fullQuestionnaire) : []
    const freshVariantMap = new Map(freshVariants.map(v => [v.name, v]))

    // For each variant, update the text format properly
    variants.forEach((variant) => {
      let variantContent = ""

      if (variant.name === currentVariant) {
        // Use the current editor content for the active variant being edited
        variantContent = currentContent
      } else {
        // For other variants, prefer Yjs doc content (CRDT-merged, latest)
        // over backend data (potentially stale from another user's save)
        const yjsContent = this.getVariantContentAsText(variant.name)
        if (yjsContent) {
          variantContent = yjsContent
        } else {
          const freshVariant = freshVariantMap.get(variant.name)
          variantContent = freshVariant?.content || variant.content || ""
        }
      }

      if (variantContent) {
        result = updateQuestionnaireWithVariant(
          result,
          variant.name,
          variantContent,
          variant.languages
        )
      }
    })

    // Safety net: ensure the active variant is always included even if
    // the variants array is temporarily stale (e.g. during a race condition)
    const currentVariantInList = variants.some(v => v.name === currentVariant)
    if (!currentVariantInList && currentVariant !== "master" && currentVariant !== "" && currentContent) {
      result = updateQuestionnaireWithVariant(result, currentVariant, currentContent, [])
    }

    return result
  }

  /**
   * Generate unique document key for WebSocket provider
   */
  private getDocumentKey(variantName: string): string {
    return `${this.config.projectId}-${variantName}`
  }

  /**
   * Set callback for document updates
   */
  setOnDocumentUpdate(
    callback: (variantName: string, content: string) => void
  ): void {
    this.onDocumentUpdate = callback
  }

  /**
   * Set callback for variant changes
   */
  setOnVariantChange(callback: (variants: Variant[]) => void): void {
    this.onVariantChange = callback
  }

  /**
   * Set callback for questionnaire refresh
   */
  setOnQuestionnaireRefresh(callback: () => void): void {
    this.onQuestionnaireRefresh = callback
  }

  /**
   * Get variants from metadata
   */
  private getVariantsFromMetadata(): Variant[] {
    if (!this.variantsDoc) return []

    const variantsMap = this.variantsDoc.getMap("variants")
    const variants: Variant[] = []

    variantsMap.forEach((value, key) => {
      if (key !== "master") {
        // Don't include master in variants list
        const metadata = value as VariantMetadata

        // For newly created variants, we don't have content yet, use empty string
        // The content will be loaded when the questionnaire is refreshed
        variants.push({
          name: metadata.name,
          languages: metadata.languages,
          content: "", // Content will be loaded from server when questionnaire refreshes
        })
      }
    })

    return variants
  }

  /**
   * Update variant metadata
   */
  updateVariantMetadata(variantName: string, languages: string[]): void {
    if (!this.variantsDoc) {
      console.error("variantsDoc not initialized")
      return
    }

    const variantsMap = this.variantsDoc.getMap("variants")
    const metadata: VariantMetadata = {
      name: variantName,
      languages,
      lastModified: new Date().toISOString(),
    }

    variantsMap.set(variantName, metadata)
  }

  /**
   * Remove variant metadata
   */
  removeVariantMetadata(variantName: string): void {
    if (!this.variantsDoc) {
      console.error("variantsDoc not initialized")
      return
    }

    const variantsMap = this.variantsDoc.getMap("variants")
    variantsMap.delete(variantName)
  }

  /**
   * Get all variant metadata
   */
  getAllVariantMetadata(): VariantMetadata[] {
    if (!this.variantsDoc) return []

    const variantsMap = this.variantsDoc.getMap("variants")
    const metadata: VariantMetadata[] = []

    variantsMap.forEach((value, key) => {
      if (key !== "master") {
        metadata.push(value as VariantMetadata)
      }
    })

    return metadata
  }

  /**
   * Check if a variant is currently connected
   */
  isVariantConnected(variantName: string): boolean {
    const variantDoc = this.variantDocs[variantName]
    return variantDoc ? variantDoc.isConnected : false
  }

  /**
   * Disconnect from a specific variant
   */
  disconnectVariant(variantName: string): void {
    const variantDoc = this.variantDocs[variantName]
    if (variantDoc) {
      variantDoc.provider?.disconnect()
      delete this.variantDocs[variantName]
    }
  }

  /**
   * Disconnect from all variants
   */
  disconnectAll(): void {
    Object.keys(this.variantDocs).forEach((variantName) => {
      this.disconnectVariant(variantName)
    })

    // Also disconnect from variants metadata
    if (this.variantsProvider) {
      this.variantsProvider.disconnect()
      this.variantsProvider = null
    }
    if (this.variantsDoc) {
      this.variantsDoc.destroy()
      this.variantsDoc = null
    }
  }

  /**
   * Get connection status for all variants
   */
  getConnectionStatus(): Record<string, boolean> {
    const status: Record<string, boolean> = {}
    Object.keys(this.variantDocs).forEach((variantName) => {
      status[variantName] = this.isVariantConnected(variantName)
    })
    return status
  }

  /**
   * Record that an HTTP save happened. If the WebSocket is disconnected,
   * sets a flag so the next page load knows the Yjs server doc is stale
   * and should be replaced with backend content.
   * Uses provider.wsconnected (actual WebSocket state) instead of our
   * isConnected flag, which can lag behind on server shutdown.
   */
  recordSaveTimestamp(variantName: string): void {
    const variantDoc = this.variantDocs[variantName]
    const isWsConnected = variantDoc?.provider?.wsconnected ?? false
    const key = `pendingSave-${this.config.projectId}-${variantName}`

    if (!isWsConnected) {
      localStorage.setItem(key, "true")
    } else {
      // Connected save — Yjs server has our edits, clear any pending flag
      localStorage.removeItem(key)
    }
  }

  /**
   * Check if an HTTP save happened while the WebSocket was disconnected.
   * If true, the Yjs server doc is stale and backend content should be preferred.
   */
  isBackendNewer(variantName: string): boolean {
    const key = `pendingSave-${this.config.projectId}-${variantName}`
    return localStorage.getItem(key) === "true"
  }

  /**
   * Clear the pending save flag (called after reconnect or after
   * checkAndPopulate successfully loads backend content).
   */
  clearPendingSave(variantName: string): void {
    const key = `pendingSave-${this.config.projectId}-${variantName}`
    localStorage.removeItem(key)
  }

  /**
   * Clear the Yjs prosemirror fragment for a variant (removes stale content).
   * Unsafe when other clients are connected to the same room — the delete
   * ops propagate and can remove content other users are editing. Guarded
   * against that case so it only fires when the caller is the sole client.
   */
  clearYjsFragment(variantName: string): void {
    const variantDoc = this.variantDocs[variantName]
    if (!variantDoc?.ydoc) return
    const onlyMeConnected =
      (variantDoc.provider?.awareness.getStates().size ?? 1) <= 1
    if (!onlyMeConnected) {
      console.warn(
        "clearYjsFragment skipped — other clients connected.",
        { variantName },
      )
      return
    }
    const fragment = variantDoc.ydoc.getXmlFragment("default")
    variantDoc.ydoc.transact(() => {
      fragment.delete(0, fragment.length)
    })
  }

  /**
   * Ask the collaboration server to clear a stale room's Yjs doc.
   * The server only clears if no other clients are connected.
   * Uses the HTTP API on the same host/port as the WebSocket server.
   */
  async clearStaleRoom(variantName: string): Promise<boolean> {
    try {
      const docKey = this.getDocumentKey(variantName)
      const httpUrl = this.config.websocketUrl
        .replace("wss://", "https://")
        .replace("ws://", "http://")
      const res = await fetch(`${httpUrl}/api/rooms/${docKey}/clear`, {
        method: "POST",
      })
      const data = await res.json()
      return data.cleared === true
    } catch (err) {
      console.error("Failed to clear stale room:", err)
      return false
    }
  }
}
